import { defineStore } from 'pinia';
import {
  computed,
  reactive,
  Ref,
  ref,
} from 'vue';
import { v4 as uuidV4 } from 'uuid';

import { EAppState, EWasAuthBy } from '@/constants';
import {
  TSessionWithTokensSerialized,
  TVatRate,
} from '@/contexts/accountingContext/domain/types';
import { TSberDataParams } from '@/components/SberBusinessIntegrationButton/domain/types';
import { TApplicationSettings } from '@/domains/types';
import { TReachedResponse } from '@/api/types';
import { getApplicationSettings } from '@/services';
import { decodeJWT } from '@/utils';
import { disconnectConsumer } from '@/websocket/controller';
import { signOut } from '@/contexts/accountingContext/services';
import { getNowMs } from '@/utils/dateUtils';
import { EVatRateV2, VAT_RATE_V2_TRANSLATION_SHORT } from '@/domains/getVatRateOptions';
import { useNotificationStore } from '@/stores/notifications';
import { useFilterStore } from '@/stores/filter';

import { DEFAULT_TOKEN_REFRESH_STATE } from './constants';
import { TAppStore, TTokenRefreshState } from './types';
import { useUserStore } from '../user';
import { useViewSettingsStore } from '../viewSettings';
import { useCustomizationStore } from '../customization';
import { useSearchStore } from '../search';
import { useExternalDigitalQueuesStore } from '../externalDigitalQueues';
import { useFoldersStore } from '../folders';
import { useDigitalQueueSettingsStore } from '../digitalQueueSettings';
import { useResourcesControlStore } from '../resourcesControl';
import { useCombinedOrdersStore } from '../CombinedOrders';
import { useWalletStore } from '../wallet';
import { useTenantsStore } from '../tenants';

export const useAppStore = defineStore('TMS_APP', (): TAppStore => {
  const appState: Ref<EAppState> = ref(EAppState.initial);
  const wasAuthBy: Ref<EWasAuthBy | null> = ref(null);
  /** Токены */
  const accessToken: Ref<string> = ref('');
  const csrfToken: Ref<string> = ref('');
  const refreshToken: Ref<string> = ref('');
  /** Срок жизни токенов */
  const accessTokenExpiresAt: Ref<string> = ref('');
  const refreshTokenExpiresAt: Ref<string> = ref('');
  const tokenRefreshState = ref<TTokenRefreshState>({ ...DEFAULT_TOKEN_REFRESH_STATE });
  const currentTabId = uuidV4();
  const isTemporaryAccessSessionSwitching = ref(false);

  const isSidebarCollapsed: Ref<boolean> = ref(false);
  const isBrowserTabFocused: Ref<boolean> = ref(false);
  const namesOfOpenedSubfolders: Ref<string[]> = ref([]);
  const sberParams: Ref<TSberDataParams | null> = ref(null);
  const appSettings: Ref<TApplicationSettings | null> = ref(null);
  const UTMSourceParams: { entryUid: string, utmSources: string[] } = reactive({
    entryUid: '',
    utmSources: [],
  });
  const routeBeforeLogout: Ref<string | null> = ref(null);
  const isAppStateLoading: Ref<boolean> = ref(false);
  const isLegislativeRemarkVisible: Ref<boolean> = ref(false);
  const availableVatRates = ref<TVatRate[] | null>(null);

  const isIntermediateAppState = computed(
    () => appState.value === EAppState.initial
        || appState.value === EAppState.checking
        || isAppStateLoading.value);

  const isAuthenticatedAppState = computed(() => appState.value === EAppState.authenticated && !!accessToken.value);
  const getDecodedJWT = computed(() => decodeJWT(accessToken.value) as { user_id?: string, entry_uid?: string } | null);

  const isAuthByEntryCode = computed(
    () => wasAuthBy.value === EWasAuthBy.entryCode || !!getDecodedJWT.value?.entry_uid);

  const isCurrentTabRefreshingToken = computed(() => currentTabId === tokenRefreshState.value.tabId);

  const getAvailableVatRatesOptions = computed<{
    value: EVatRateV2,
    label: string,
  }[] | undefined>(() => availableVatRates.value?.map(
    (option: TVatRate) => ({
      value: option.vatRateSlug,
      label: VAT_RATE_V2_TRANSLATION_SHORT.value[option.vatRateSlug],
    })));

  const setAppState = (state: EAppState) => {
    appState.value = state;
  };

  const setAuthTokens = (tokens: TSessionWithTokensSerialized) => {
    accessToken.value = tokens.accessToken;
    accessTokenExpiresAt.value = tokens.accessTokenExpiresAt;
    csrfToken.value = tokens.csrfToken;

    if (tokens?.refreshToken) {
      refreshToken.value = tokens.refreshToken;
      refreshTokenExpiresAt.value = tokens.refreshTokenExpiresAt;
    }
  };

  const setTokenRefreshState = (value: TTokenRefreshState) => {
    tokenRefreshState.value = value;
  };

  const setTokenRefreshHeartbeat = () => {
    if (!tokenRefreshState.value.isRefreshing) return;
    tokenRefreshState.value.heartbeatAt = getNowMs();
  };

  const resetRefreshingProcess = () => {
    if (!tokenRefreshState.value.isRefreshing) return;
    tokenRefreshState.value = { ...DEFAULT_TOKEN_REFRESH_STATE };
  };

  const setTemporaryAccessSessionSwitchingState = (value: boolean) => {
    isTemporaryAccessSessionSwitching.value = value;
  };

  const setWasAuthBy = (authBy: EWasAuthBy | null) => {
    wasAuthBy.value = authBy;
  };

  const setSidebarState = (isCollapsed: boolean) => {
    isSidebarCollapsed.value = isCollapsed;
  };

  const setBrowserTabState = (isFocused: boolean) => {
    isBrowserTabFocused.value = isFocused;
  };

  const setNamesOfOpenedSubfolders = (names: string[]) => {
    namesOfOpenedSubfolders.value = names;
  };

  const setSberParams = (params: TSberDataParams | null) => {
    sberParams.value = params;
  };

  const setViewSettingsMaxColumns = (maxColumns: number) => {
    if (!appSettings.value) return;
    appSettings.value.settings.viewSettingsMaxColumns = maxColumns;
  };

  const setUTMSourceParams = (params: { key: string, value: string }) => {
    if (params.key === 'entryUid') {
      UTMSourceParams.entryUid = accessToken.value
        ? (decodeJWT(accessToken.value) as any)?.entry_uid
        : null;
    } else if (params.key === 'utmSources' && params.value) {
      UTMSourceParams.utmSources.push(params.value);
    }
  };

  const setRouteBeforeLogout = (route: string | null) => {
    routeBeforeLogout.value = route;
  };

  const setLoadingState = (isLoading: boolean) => {
    isAppStateLoading.value = isLoading;
  };

  const setIsLegislativeRemarkVisible = (isVisible: boolean) => {
    isLegislativeRemarkVisible.value = isVisible;
  };

  const setAvailableVatRates = (vatRates: TVatRate[] | null) => {
    availableVatRates.value = vatRates;
  };

  /** Загрузить настройки приложения */
  const loadAppSettings = async () => {
    const response: TReachedResponse<TApplicationSettings> = await getApplicationSettings();
    if (!response?.data) return;
    appSettings.value = response.data;
  };

  const $reset = () => {
    setUTMSourceParams({
      key: 'entryUid',
      value: '',
    });

    useFoldersStore().$reset();
    useViewSettingsStore().$reset();
    useCustomizationStore().$reset();
    useSearchStore().$reset();
    useExternalDigitalQueuesStore().$reset();
    useDigitalQueueSettingsStore().$reset();
    useResourcesControlStore().$reset();
    useCombinedOrdersStore().$reset();
    useTenantsStore().$reset();
    useWalletStore().$reset();
    useUserStore().$reset();
    useNotificationStore().$reset();
    useFilterStore().$reset();

    setAuthTokens({
      accessToken: '',
      accessTokenExpiresAt: '',
      refreshToken: '',
      refreshTokenExpiresAt: '',
      csrfToken: '',
    });
    refreshToken.value = '';
    refreshTokenExpiresAt.value = '';
    resetRefreshingProcess();
    setAppState(EAppState.unauthenticated);
  };

  /** Выйти из системы */
  const logout = async () => {
    if (!refreshToken.value) return;
    await disconnectConsumer();
    try {
      signOut({ refreshToken: refreshToken.value });
    } finally {
      $reset();
    }
  };

  return {
    appState,
    wasAuthBy,
    accessToken,
    csrfToken,
    refreshToken,
    accessTokenExpiresAt,
    refreshTokenExpiresAt,
    tokenRefreshState,
    currentTabId,
    isSidebarCollapsed,
    isBrowserTabFocused,
    namesOfOpenedSubfolders,
    sberParams,
    appSettings,
    UTMSourceParams,
    routeBeforeLogout,
    availableVatRates,
    isAppStateLoading,
    isLegislativeRemarkVisible,
    isTemporaryAccessSessionSwitching,

    isCurrentTabRefreshingToken,
    isIntermediateAppState,
    isAuthenticatedAppState,
    isAuthByEntryCode,
    getDecodedJWT,
    getAvailableVatRatesOptions,

    setAppState,
    setAuthTokens,
    setTokenRefreshState,
    setTokenRefreshHeartbeat,
    resetRefreshingProcess,
    setTemporaryAccessSessionSwitchingState,
    setWasAuthBy,
    setSidebarState,
    setBrowserTabState,
    setNamesOfOpenedSubfolders,
    setSberParams,
    setViewSettingsMaxColumns,
    setUTMSourceParams,
    setRouteBeforeLogout,
    setLoadingState,
    setIsLegislativeRemarkVisible,
    setAvailableVatRates,
    $reset,

    loadAppSettings,
    logout,
  };
}, {
  persist: {
    pick: [
      'wasAuthBy',
      'routeBeforeLogout',
      'accessToken',
      'csrfToken',
      'refreshToken',
      'accessTokenExpiresAt',
      'refreshTokenExpiresAt',
      'tokenRefreshState',
      'isSidebarCollapsed',
      'namesOfOpenedSubfolders',
      'appSettings',
      'UTMSourceParams',
      'availableVatRates',
      'isLegislativeRemarkVisible',
      'isTemporaryAccessSessionSwitching',
      'isAuthByEntryCode',
    ],
  },
});
